import { inject, Injectable } from '@angular/core'
import {
    EmployeeLabourCodeInterface,
    EmployeesInterface,
    EmployeeTypesInterface
} from '@isl-features/employees/interfaces/employees.interface'
import { LeaveTypesInterface } from '@isl-features/employees/interfaces/leaves.interface'
import { SkillsInterface } from '@isl-features/employees/interfaces/skills.interface'
import { ShipTypesInterface } from '@isl-features/ships/interfaces/ship-types.interface'
import { ShipsInterface } from '@isl-features/ships/interfaces/ships.interface'
import { LabourSheetCounterInterface, ReportPeriodInterface } from '@isl-shared/interfaces/app.interface'
import { EmptyParamsInterface } from '@isl-shared/interfaces/empty-params.interface'
import { urlParseTemplate } from '@isl-utils/url.util'
import { forkJoin } from 'rxjs'
import { AppDataService } from './app-data.service'
import { HttpService } from './http.service'

@Injectable({
    providedIn: 'root'
})
export class DataService {
    private httpService = inject(HttpService)
    private appDataService = inject(AppDataService)

    async initData(): Promise<boolean> {
        const employees = this.getEmployees()
        const employeeTypes = this.getEmployeeTypes()
        const skills = this.getSkills()
        const leaveTypes = this.getLeaveTypes()
        const ships = this.getShips()
        const shipTypes = this.getShipTypes()
        const labourSheetCounters = this.getLabourSheetCounters()
        const reportPeriods = this.getReportPeriods()
        const employeeLabourCodes = this.getEmployeeLabourCodes()

        const currentYear = new Date().getFullYear()
        const reportPeriodsPreviousYear = this.getReportPeriodsByYear((currentYear - 1).toString())
        const reportPeriodsNextYear = this.getReportPeriodsByYear((currentYear + 1).toString())

        return new Promise((resolve) => {
            forkJoin({
                employees,
                employeeTypes,
                skills,
                leaveTypes,
                ships,
                shipTypes,
                labourSheetCounters,
                reportPeriods,
                employeeLabourCodes,
                reportPeriodsPreviousYear,
                reportPeriodsNextYear
            }).subscribe({
                next: ({
                    employees,
                    employeeTypes,
                    skills,
                    leaveTypes,
                    ships,
                    shipTypes,
                    labourSheetCounters,
                    reportPeriods,
                    employeeLabourCodes,
                    reportPeriodsPreviousYear,
                    reportPeriodsNextYear
                }) => {
                    employees = employees.map((employee) => ({
                        ...employee,
                        fullName: `${employee.firstName} ${employee.lastName}`
                    }))
                    this.appDataService.setEmployeeTypes(employeeTypes)
                    this.appDataService.setEmployee(employees)
                    this.appDataService.setEmployeeActive(employees)
                    this.appDataService.setSkills(skills)
                    this.appDataService.setLeaveTypes(leaveTypes)
                    this.appDataService.setShips(ships)
                    this.appDataService.setShipTypes(shipTypes)
                    this.appDataService.setLabourSheetCounters(labourSheetCounters)
                    this.appDataService.setReportPeriods(reportPeriods)
                    this.appDataService.setEmployeeLabourCodes(employeeLabourCodes)
                    this.appDataService.setAddReportPeriods(reportPeriodsPreviousYear)
                    this.appDataService.setAddReportPeriods(reportPeriodsNextYear)

                    return resolve(true)
                }
            })
        })
    }

    public getEmployees(): Promise<EmployeesInterface[]> {
        const employeesUrl = urlParseTemplate(this.httpService.getUrl.employees, {})
        const employees = this.httpService.get<EmptyParamsInterface, EmployeesInterface[]>(employeesUrl, {})
        return employees
    }

    private getEmployeeTypes(): Promise<EmployeeTypesInterface[]> {
        const employeeTypesUrl = urlParseTemplate(this.httpService.getUrl.employeeTypes, {})
        const employeeTypes = this.httpService.get<EmptyParamsInterface, EmployeeTypesInterface[]>(employeeTypesUrl, {})

        return employeeTypes
    }

    public async refreshEmployees(): Promise<boolean> {
        const employees = await this.getEmployees()
        this.appDataService.setEmployee(employees)
        this.appDataService.setEmployeeActive(employees)
        return true
    }

    public getSkills(): Promise<SkillsInterface[]> {
        const skillsUrl = urlParseTemplate(this.httpService.getUrl.skills, {})
        const skills = this.httpService.get<EmptyParamsInterface, SkillsInterface[]>(skillsUrl, {})

        return skills
    }

    public async refreshSkills(): Promise<boolean> {
        const skills = await this.getSkills()
        this.appDataService.setSkills(skills)
        return true
    }

    private getLeaveTypes(): Promise<LeaveTypesInterface[]> {
        const leaveTypesUrl = urlParseTemplate(this.httpService.getUrl.leaveType, {})
        const leaveTypes = this.httpService.get<EmptyParamsInterface, LeaveTypesInterface[]>(leaveTypesUrl, {})

        return leaveTypes
    }

    public getShips(): Promise<ShipsInterface[]> {
        const shipsUrl = urlParseTemplate(this.httpService.getUrl.ships, {})
        const ships = this.httpService.get<EmptyParamsInterface, ShipsInterface[]>(shipsUrl, {})

        return ships
    }

    public async refreshShips(): Promise<boolean> {
        const ships = await this.getShips()
        this.appDataService.setShips(ships)
        return true
    }

    public getShipTypes(): Promise<ShipTypesInterface[]> {
        const shipTypesUrl = urlParseTemplate(this.httpService.getUrl.shipTypes, {})
        const shipTypes = this.httpService.get<EmptyParamsInterface, ShipTypesInterface[]>(shipTypesUrl, {})

        return shipTypes
    }

    public async refreshShipTypes(): Promise<boolean> {
        const shipTypes = await this.getShipTypes()
        this.appDataService.setShipTypes(shipTypes)
        return true
    }

    private getLabourSheetCounters(): Promise<LabourSheetCounterInterface[]> {
        const labourSheetCountersUrl = urlParseTemplate(this.httpService.getUrl.labourSheetCounters, {})
        const labourSheetCounters = this.httpService.get<EmptyParamsInterface, LabourSheetCounterInterface[]>(
            labourSheetCountersUrl,
            {}
        )

        return labourSheetCounters
    }

    public async refreshLabourSheetCounters(): Promise<boolean> {
        const labourSheetCounters = await this.getLabourSheetCounters()
        this.appDataService.setLabourSheetCounters(labourSheetCounters)
        return true
    }

    private getReportPeriods(): Promise<ReportPeriodInterface[]> {
        const reportPeriodsUrl = urlParseTemplate(this.httpService.getUrl.reportPeriods, {})
        const reportPeriods = this.httpService.get<EmptyParamsInterface, ReportPeriodInterface[]>(reportPeriodsUrl, {})

        return reportPeriods
    }

    public getReportPeriodsByYear(year = ''): Promise<ReportPeriodInterface[]> {
        const reportPeriodsUrl = urlParseTemplate(this.httpService.getUrl.reportPeriods, {})
        const reportPeriods = this.httpService.get<{ year: string }, ReportPeriodInterface[]>(reportPeriodsUrl, {
            year
        })

        return reportPeriods
    }

    private getEmployeeLabourCodes(): Promise<EmployeeLabourCodeInterface[]> {
        const employeeLabourCodesUrl = urlParseTemplate(this.httpService.getUrl.employeeLabourCodes, {})
        const employeeLabourCodes = this.httpService.get<EmptyParamsInterface, EmployeeLabourCodeInterface[]>(
            employeeLabourCodesUrl,
            {}
        )

        return employeeLabourCodes
    }
}
